import { useIsMutating } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  GitPullRequest,
  Maximize2,
  Minimize2,
  RefreshCw,
  RotateCw,
  Save,
  Square,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { useAuth } from '@/features/auth/use-auth';
import { ApiRequestError } from '@/shared/api/api-error';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { LoadingStatus, Spinner } from '@/shared/ui/spinner';
import { UsageBalance } from '@/shared/usage/usage-balance';
import { useEditorFullscreen } from '@/shared/workspace/use-editor-fullscreen';

import { prepareFieldSignOut } from '../api/prepare-field-sign-out';
import {
  FIELD_SWITCH_MUTATION_KEY,
  useFieldWorkspace,
  useFieldWorkspaceCommand,
  useFieldWorkspaceLaunch,
  useFieldWorkspaceSwitch,
} from '../api/use-field-workspace';
import { fieldErrorMessage } from '../field-view';

type WorkspaceAction = 'activate' | 'save' | 'stop';
/** Retry a launch refused by a previous editor session every ten seconds for five minutes. */
const CONFLICT_RETRY_MS = 10_000;
const CONFLICT_RETRY_WINDOW_MS = 5 * 60_000;

function workspaceErrorMessage(error: unknown, action: WorkspaceAction = 'activate') {
  const connectionMessage =
    action === 'activate'
      ? 'Could not connect to your coding environment. Retry starting the workspace.'
      : 'Could not connect to your coding environment. Keep the editor open, then retry saving your files.';
  const fileLimitMessage =
    'Your project is too large to save. Move generated files out of the source folder, then retry.';
  const reasons: Record<string, string> = {
    workspace_bridge_unavailable: connectionMessage,
    workspace_unavailable: connectionMessage,
    artifact_digest_mismatch:
      'Your project files could not be verified. Retry the workspace action. If this continues, contact your instructor.',
    field_checkpoint_invalid:
      'The checkpoint could not be verified. Current files were not replaced. Retry the workspace action. If this continues, contact your instructor.',
    workspace_files_changed:
      'Your files changed during the save. Let file changes finish, then save again.',
    project_too_large: fileLimitMessage,
    project_file_too_large: fileLimitMessage,
    project_too_many_files: fileLimitMessage,
    project_too_many_entries: fileLimitMessage,
    field_workspace_unavailable:
      'The coding environment is temporarily unavailable. Try again shortly or contact your instructor.',
    workspace_starting: 'Your workspace is still starting. Wait a moment, then refresh.',
    workspace_stopping: 'Your workspace is still stopping. Wait a moment, then refresh.',
    capacity_full: 'All coding environments are in use. Try again after another workspace stops.',
    launch_unavailable: 'The editor could not open. Use Reconnect IDE to try again.',
    editor_disconnected:
      'Reconnect your editor before saving. Keep it open until the save finishes.',
    workspace_other_domain_active:
      'Your Workshop is still running. Use Save Workshop and start Workbench, then try again.',
    editor_changed:
      'Your Workshop editor is still open in another tab or window. Save and close it there, or wait about two minutes for it to clear, then try again.',
    editor_in_use:
      'Your editor is still open in another tab or window. Save and close it there, or wait about two minutes for it to clear; we keep retrying meanwhile.',
    editor_session_changed:
      'Your editor session changed. We keep retrying; reopen the IDE here if it does not recover.',
    editor_context_changed: 'Your editor session changed while switching. Try again in a moment.',
    workspace_busy: 'Wait for the current Workshop save to finish, then try again.',
    workspace_context_changed: 'Your Workshop changed while switching. Refresh and try again.',
    workspace_stop_failed:
      'Your Workshop could not be paused. Open Workshop to save it, then try again.',
    workspace_already_ready:
      'Your Workbench workspace is already running. Reconnect the IDE to open it.',
    workspace_start_timeout:
      'Your workspace is taking longer than usual to start. Wait a moment, then retry starting it.',
  };
  const code =
    error instanceof ApiRequestError ? error.code : typeof error === 'string' ? error : '';
  if (reasons[code]) return reasons[code];
  if (error instanceof Error && !/^[a-z]+(?:_[a-z0-9]+)+$/.test(error.message))
    return fieldErrorMessage(error);
  return action === 'activate'
    ? 'Could not start your coding environment. Retry starting the workspace.'
    : 'Your files could not be saved. Keep the editor open and retry saving.';
}

export function FieldWorkspacePanel({
  visible,
  onClose,
  onSubmit,
  submissionBusy = false,
}: {
  visible: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
  submissionBusy?: boolean;
}) {
  const { registerSignOutGuard, signingOut } = useAuth();
  useEffect(() => registerSignOutGuard(prepareFieldSignOut), [registerSignOutGuard]);
  const switching = useFieldWorkspaceSwitch();
  // The switch spans several server operations; track it in the mutation cache so a
  // re-rendered panel never shows the Workshop as active while it is still being paused.
  const switchInFlight = useIsMutating({ mutationKey: FIELD_SWITCH_MUTATION_KEY }) > 0;
  const query = useFieldWorkspace(visible, switchInFlight);
  const state = query.data;
  const command = useFieldWorkspaceCommand(state);
  const launch = useFieldWorkspaceLaunch(state);
  const [recover, setRecover] = useState(false);
  const [reconnectContext, setReconnectContext] = useState<string | null>(null);
  const [dismissedOperation, setDismissedOperation] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);
  const submitted = useRef<string | null>(null);
  const autoLaunched = useRef<string | null>(null);
  const frame = `mio-field-${useId()}`;
  const [opened, setOpened] = useState<string | null>(null);
  const context = state?.context ? JSON.stringify(state.context) : null;
  const operationBusy = Boolean(
    state?.operation && ['pending', 'running'].includes(state.operation.status),
  );
  // Periodic backups and idle sweeps run beside the learner; they never lock the editor.
  const backgroundSaving = operationBusy && state?.operation?.maintenance === true;
  const pending =
    signingOut ||
    command.isPending ||
    switching.isPending ||
    switchInFlight ||
    (operationBusy && !backgroundSaving);
  const ready = state?.selection === 'field' && state.state === 'ready';
  const editorOpen = Boolean(ready && context && opened === context);
  // Failed Workshop compute has nothing left to save; Field may start over it directly.
  const workshopActive =
    state?.selection === 'workshop' && !['stopped', 'failed'].includes(state.state);
  useEffect(() => {
    const data = launch.data;
    if (
      !visible ||
      !data ||
      JSON.stringify(data.context) !== context ||
      !ready ||
      pending ||
      !form.current ||
      submitted.current === data.launch.grant
    )
      return;
    submitted.current = data.launch.grant;
    form.current.submit();
    setOpened(context);
  }, [launch.data, context, ready, pending, visible]);
  // A ready Field workspace opens its editor as soon as the pane is shown, once per
  // context: after a switch or a resume the learner should not have to ask for the IDE.
  const { mutate: requestLaunch, isPending: launchPending, error: launchError } = launch;
  const launchedContext = launch.data ? JSON.stringify(launch.data.context) : null;
  useEffect(() => {
    if (
      !visible ||
      !ready ||
      !context ||
      pending ||
      launchPending ||
      launchError ||
      query.error ||
      launchedContext === context ||
      autoLaunched.current === context
    )
      return;
    autoLaunched.current = context;
    requestLaunch(false);
  }, [
    visible,
    ready,
    context,
    pending,
    launchPending,
    launchError,
    query.error,
    launchedContext,
    requestLaunch,
  ]);
  const error = query.error ?? command.error ?? launch.error ?? switching.error;
  // Only a failure of something the learner asked for needs their attention; a background
  // backup that failed is retried by the next sweep.
  const failedOperation =
    state?.operation?.status === 'failed' &&
    state.operation.maintenance !== true &&
    state.operation.id !== dismissedOperation
      ? state.operation
      : null;
  const failedStart =
    !ready && (state?.state === 'failed' || failedOperation?.action === 'activate');
  const editorConflict =
    launch.error instanceof ApiRequestError &&
    ['editor_in_use', 'editor_session_changed'].includes(launch.error.code);
  // A launch refused because another browser still holds this editor clears on its own once
  // that browser has been absent for about two minutes: retry ordinary admission through that
  // window instead of leaving the learner with a dead button.
  const conflictRetry = useRef<{ context: string; startedAt: number } | null>(null);
  useEffect(() => {
    if (!editorConflict) {
      conflictRetry.current = null;
      return;
    }
    if (!visible || !ready || !context || pending || launchPending || recover) return;
    if (conflictRetry.current?.context !== context)
      conflictRetry.current = { context, startedAt: Date.now() };
    const remaining = CONFLICT_RETRY_WINDOW_MS - (Date.now() - conflictRetry.current.startedAt);
    if (remaining <= 0) return;
    const timer = setTimeout(() => requestLaunch(false), Math.min(CONFLICT_RETRY_MS, remaining));
    return () => clearTimeout(timer);
  }, [editorConflict, visible, ready, context, pending, launchPending, recover, requestLaunch]);
  const pendingAction = command.isPending ? command.variables : state?.operation?.action;
  const workingMessage = signingOut
    ? 'Signing out…'
    : switching.isPending || switchInFlight
      ? 'Saving your Workshop and starting Workbench… This can take a couple of minutes if your Workshop editor is still open in another tab.'
      : pendingAction === 'activate'
        ? 'Starting your coding environment…'
        : 'Saving your files. Keep the editor connected until this finishes.';
  const status = pending
    ? 'Working'
    : query.error
      ? 'Connection lost'
      : ready
        ? backgroundSaving
          ? 'Backing up…'
          : 'Ready'
        : !state
          ? 'Connecting'
          : !state.enabled
            ? 'Unavailable'
            : workshopActive
              ? 'Workshop active'
              : failedStart
                ? 'Start failed'
                : 'Stopped';

  const fullscreen = useEditorFullscreen(visible);
  return (
    <aside
      aria-label="Workbench coding environment"
      className={`bg-canvas flex h-full min-h-0 min-w-0 flex-col overflow-hidden ${fullscreen.className}`}
    >
      {fullscreen.fullscreen ? (
        <UsageBalance
          compact
          onManage={fullscreen.exit}
          className="border-line shrink-0 border-b px-4 py-2"
        />
      ) : null}
      <header className="border-line flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-sm font-semibold">Editor</h2>
          <span className="text-fg-muted truncate text-xs" role="status">
            {status}
          </span>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-1">
          {onSubmit ? (
            <Button size="sm" disabled={pending || submissionBusy} onClick={onSubmit}>
              <GitPullRequest className="size-3.5" aria-hidden="true" />
              Submit for review
            </Button>
          ) : null}
          {ready ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 px-2"
                disabled={pending || Boolean(query.error)}
                onClick={() => command.mutate('save')}
                title="Save a checkpoint, including connected editor buffers"
              >
                <Save className="size-3.5" aria-hidden="true" />
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Save and stop"
                title="Save and stop workspace"
                disabled={pending || Boolean(query.error)}
                onClick={() => command.mutate('stop')}
              >
                <Square className="size-3.5" aria-hidden="true" />
                Save & stop
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 px-2"
                disabled={pending || Boolean(query.error)}
                loading={launch.isPending}
                onClick={() => {
                  if (editorOpen) setReconnectContext(context);
                  else launch.mutate(false);
                }}
                title="Reload the editor connection. Save any open files first."
              >
                <RotateCw className="size-3.5" aria-hidden="true" />
                Reconnect IDE
              </Button>
            </>
          ) : null}
          <Button
            size="icon"
            variant="ghost"
            aria-pressed={fullscreen.fullscreen}
            aria-label={fullscreen.fullscreen ? 'Exit full screen' : 'Full screen editor'}
            title={
              fullscreen.fullscreen
                ? 'Exit full screen (Esc)'
                : 'Expand the editor to the whole window'
            }
            onClick={fullscreen.toggle}
          >
            {fullscreen.fullscreen ? (
              <Minimize2 className="size-3.5" aria-hidden="true" />
            ) : (
              <Maximize2 className="size-3.5" aria-hidden="true" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Refresh workspace"
            title="Check workspace status without reloading the editor"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            <RefreshCw
              className={`size-3.5 ${query.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </Button>
          {onClose ? (
            <Button
              size="icon"
              variant="ghost"
              aria-label="Hide editor"
              title="Hide editor — your workspace stays connected"
              onClick={() => {
                fullscreen.exit();
                onClose();
              }}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      </header>

      {error || failedOperation ? (
        <div className="max-h-[40%] shrink-0 space-y-2 overflow-y-auto p-3">
          {error ? (
            <Alert tone={editorConflict && error === launch.error ? 'info' : 'danger'}>
              {workspaceErrorMessage(error, command.variables ?? (ready ? 'save' : 'activate'))}
            </Alert>
          ) : null}
          {failedOperation ? (
            <Alert>
              <p>{workspaceErrorMessage(failedOperation.error, failedOperation.action)}</p>
              {state?.checkpointAt ? (
                <p className="mt-1 text-xs">Your last saved checkpoint is still available.</p>
              ) : null}
              <button
                type="button"
                className="mt-1 text-xs font-medium underline underline-offset-2"
                onClick={() => setDismissedOperation(failedOperation.id)}
              >
                Dismiss
              </button>
            </Alert>
          ) : null}
          {editorConflict ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-xs">
                We keep retrying while the previous editor clears. Reopening here restores saved
                files only; save and close the previous IDE first if it is still open.
              </p>
              <Button size="sm" variant="secondary" onClick={() => setRecover(true)}>
                Reopen IDE here
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmationDialog
        open={visible && recover}
        title="Reopen Workbench IDE here?"
        confirmLabel="Reopen saved files"
        cancelLabel="Cancel"
        busy={launch.isPending}
        onCancel={() => setRecover(false)}
        onConfirm={() => {
          setRecover(false);
          launch.mutate(true);
        }}
      >
        Close the previous editor first. Unsaved changes there cannot be recovered; a connected
        editor cannot be taken over.
      </ConfirmationDialog>
      <ConfirmationDialog
        open={visible && ready && context !== null && reconnectContext === context}
        title="Reconnect Workbench IDE?"
        confirmLabel="Reconnect IDE"
        cancelLabel="Keep editor open"
        busy={launch.isPending}
        confirmDisabled={pending || Boolean(query.error)}
        onCancel={() => setReconnectContext(null)}
        onConfirm={() => {
          if (!ready || pending || launch.isPending || query.error || reconnectContext !== context)
            return;
          setReconnectContext(null);
          launch.mutate(false);
        }}
      >
        Save any open files in the editor before continuing. Reconnecting reloads the editor;
        unsaved changes may be lost. Your saved project files stay in this workspace.
      </ConfirmationDialog>
      {launch.data && JSON.stringify(launch.data.context) === context ? (
        <form ref={form} method="POST" action={launch.data.launch.action} target={frame} hidden>
          <input type="hidden" name="grant" value={launch.data.launch.grant} />
        </form>
      ) : null}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <iframe
          title="Workbench IDE"
          style={{ colorScheme: 'inherit' }}
          name={frame}
          sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups"
          referrerPolicy="no-referrer"
          allow="clipboard-read; clipboard-write"
          inert={!visible || pending || !editorOpen}
          className={`h-full w-full border-0 ${editorOpen ? '' : 'hidden'}`}
        />
        {pending ? (
          <div className="bg-canvas/95 absolute inset-0 flex flex-col items-center justify-center gap-3 overflow-y-auto p-6 text-center">
            <div className="w-full max-w-xs space-y-5 text-left">
              <LoadingStatus label={workingMessage} />
              <ProgressBar value={null} label="Workbench workspace progress" />
            </div>
          </div>
        ) : !editorOpen ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center overflow-y-auto p-6 text-center">
            {!state && !query.error ? <Spinner className="text-fg-muted mb-4 size-6" /> : null}
            <h3 className="text-base font-semibold">
              {!state
                ? 'Connecting to your workspace'
                : !state.enabled
                  ? 'Code in your own environment'
                  : workshopActive
                    ? 'Your Workshop is still running'
                    : ready
                      ? 'Your editor is ready'
                      : failedStart
                        ? 'Let’s get your workspace running'
                        : 'Your working repository'}
            </h3>
            <p className="text-fg-muted mt-2 max-w-sm text-sm leading-6">
              {!state
                ? 'Checking your saved files and workspace availability.'
                : !state.enabled
                  ? 'Use Download starter above, then submit your changed source files in Pull requests.'
                  : workshopActive
                    ? 'Workbench uses the same coding environment. Pause your Workshop to start Workbench here: files already saved to disk are backed up first, and Workshop resumes later where you left off.'
                    : ready
                      ? 'Open your files, terminal, and preview alongside the company.'
                      : 'Start your coding environment to build, test, and submit your work.'}
            </p>
            {workshopActive ? (
              <>
                <Button
                  size="sm"
                  className="mt-5"
                  loading={switching.isPending}
                  disabled={Boolean(query.error)}
                  onClick={() => switching.mutate()}
                >
                  Save Workshop and start Workbench
                </Button>
                <Link to="/workshop" className="text-link mt-3 text-xs font-medium">
                  Open Workshop instead
                </Link>
              </>
            ) : state?.enabled ? (
              <Button
                size="sm"
                className="mt-5"
                loading={launch.isPending}
                disabled={Boolean(query.error)}
                onClick={() => (ready ? launch.mutate(false) : command.mutate('activate'))}
              >
                {ready ? 'Open IDE' : failedStart ? 'Retry start' : 'Start Workbench workspace'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <footer className="border-line text-fg-muted flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t px-4 py-2 text-xs">
        <span
          className="min-w-0"
          title="Files are checkpointed every five minutes. After 30 minutes without editor interaction the workspace saves disk files and stops. Keep the editor connected to include unsaved buffers."
        >
          Checkpoints every 5 min
        </span>
        <span className="shrink-0 tabular-nums">
          {state?.checkpointAt
            ? `Saved ${new Date(state.checkpointAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : state
              ? 'No checkpoint saved yet'
              : 'Checking saved files…'}
        </span>
      </footer>
    </aside>
  );
}
